/**
 * Sense → Today, the screen itself. Slice 62. `TodayPage` fetches and acts;
 * this draws, so `verify-sense-screens.tsx` can render it outside Vite.
 *
 * ## WHAT THE PAGE IS FOR
 *
 * The pillar's landing: the few things worth doing, most valuable first, each
 * with one action. A row is a sentence (what is true), where it comes from, the
 * rupees at stake with how far to trust them, the arithmetic one tap away, and
 * two buttons — the action, and "Not now".
 *
 * ## RANKED, THEN "NOT RANKED YET"
 *
 * Ranked rows lead with raw rupees and the tier badge side by side; the order
 * discounts by tier, the figure does not, so a reader can see why a Correlated
 * ₹40,000 sits below a Matched ₹20,000. Rows with no rupee figure are a
 * separate band under its own heading — never a ₹0 at the bottom of the ranked
 * list, which would call them worthless when the truth is that their worth is
 * not known yet.
 *
 * ## TAKING AND DISMISSING
 *
 * Both write to the action ledger and take the row off the queue for 30 days;
 * the page says so in one line under the queue, with the count, and links to
 * the ledger. Nothing here sends: the furthest an action goes is a form, or
 * (slice 70) a DRAFT flow the person opens, checks and launches themselves.
 *
 * ## WHICH PILLAR AN ACTION HANDS TO — SLICE 65
 *
 * Sense finds; another pillar acts. Beside each action sits a small brand
 * badge naming that pillar — "Connect" for every finding that ends in a
 * segment, a campaign or a saved audience, which is owned-channel work, and
 * since slice 70 "Flow" for the three flow kinds (two draft a flow, one reads
 * a live flow's holdout) — so a person can see before pressing whose work the
 * button starts. Until slice 70 this said the badge was always Connect; that
 * was true while Flow was not built, and is superseded. It names the pillar, not the screen: "Open the segment" lands
 * on Segments, a shared layer under no pillar, and the work it starts is
 * still Connect's. The map is `FINDING_PILLAR` in `@wend/shared`, total over
 * the finding kinds, so a future ad finding cannot reach this screen without
 * somebody writing `'reach'` beside it; the name is `PILLAR_LABELS`, the
 * sidebar's own word.
 *
 * It is a badge and not part of the button's label on purpose. "Open the
 * segment in Connect" would lengthen five buttons to say one fact, and the
 * button's accessible name is already the action plus the finding's title.
 * The badge reads "For Connect", in words every reader gets. The first cut
 * printed the bare name with "Hands to" for screen readers only, and review
 * caught what a sighted reader saw: an uppercase "CONNECT" between two
 * buttons, which reads as a verb — "connect something" — or as a third
 * button. Not "Opens in": half the actions open a shared screen, and that
 * would be false. The brand
 * tone is the one tone that is not a verdict, which matters on a row whose
 * other badge is a trust tier in good, info or warn.
 *
 * ## THE FIRST ROW IS A BRAND CALLOUT — SLICE 66
 *
 * The Blueprint draws a screen's lead finding as its own object, not as the
 * first of a stack of identical cards. Here that is a `Callout tone="brand"`
 * titled "Start here": violet because violet is Wend talking and cannot be
 * misread as good news or a warning — the trust tier beside the rupees is the
 * only verdict on the row. It is the same row as every other (same sentence,
 * figure, tier, badge and buttons, in the same order) inside a different
 * frame, and it is the row that already carried the page's one primary
 * button, so the frame and the primary agree about where to begin. The
 * rejected alternative was a ring or a brighter border on the first card,
 * which says "selected" — a state this list does not have.
 *
 * The rest of each row takes the Blueprint's type roles: the source is a mono
 * kicker, "at stake" is a kicker under its figure, and the rupees are a sans
 * tabular figure — mono's dotted zero reads as an eight at a glance, which on
 * a rupee amount is the one misreading this screen cannot have.
 */
import { Link } from 'react-router-dom';

import {
  ATTRIBUTION_TIER_LABEL,
  FINDING_PILLAR,
  TODAY_QUIET_DAYS,
  type AttributionTier,
  type Finding,
  type Today,
} from '@wend/shared';
import { Badge, Button, Callout, Card, CardContent, Kicker, type BadgeTone } from '@wend/ui';

import { formatCount, formatRupees } from '../../lib/format';
import { PILLAR_LABELS } from '../../lib/pillars';
import { MoreDetail } from '../templates/MoreDetail';

/**
 * Trust, said in colour as well as a word: Matched is a measurement, Correlated
 * a typical person's value applied to a group. The concept draws the same split.
 */
const TIER_TONE: Readonly<Record<AttributionTier, BadgeTone>> = {
  matched: 'good',
  coded: 'info',
  correlated: 'warn',
  declared: 'neutral',
};

export interface TodayHandlers {
  readonly onTake: (finding: Finding) => void;
  readonly onDismiss: (finding: Finding) => void;
  /** The fingerprint being acted on, if any — its buttons wait. */
  readonly busy: string | null;
}

export function TodayContent({ today, handlers }: { today: Today; handlers: TodayHandlers }) {
  const nothing = today.ranked.length === 0 && today.unranked.length === 0;

  return (
    <>
      {nothing ? (
        <p className="text-[0.92rem] text-muted" data-role="today-empty">
          Nothing to act on today.
        </p>
      ) : (
        <>
          {today.ranked.length > 0 && (
            <section className="flex flex-col gap-3" aria-labelledby="today-ranked-title" data-role="today-ranked">
              <h2 id="today-ranked-title" className="text-[0.95rem] font-medium text-text">
                Worth doing · by ₹ at stake
              </h2>
              <ol className="flex flex-col gap-3">
                {today.ranked.map((finding, index) => (
                  <FindingRow key={finding.fingerprint} finding={finding} handlers={handlers} lead={index === 0} />
                ))}
              </ol>
            </section>
          )}
          {today.unranked.length > 0 && (
            <section className="flex flex-col gap-3" aria-labelledby="today-unranked-title" data-role="today-unranked">
              <h2 id="today-unranked-title" className="text-[0.95rem] font-medium text-text">
                Not ranked yet
              </h2>
              <ul className="flex flex-col gap-3">
                {today.unranked.map((finding, index) => (
                  <FindingRow
                    key={finding.fingerprint}
                    finding={finding}
                    handlers={handlers}
                    lead={today.ranked.length === 0 && index === 0}
                  />
                ))}
              </ul>
              <MoreDetail summary="Why these have no ₹ figure">
                {today.baselineCovered ? (
                  <p>
                    Templates and segments are not people, and a campaign with no clear difference from its held-back
                    group has no rupees to put on it.
                  </p>
                ) : (
                  <p>
                    No list of who converted covers every day of the last {String(today.windowDays)} days, so Wend
                    cannot say what a person in your audience is worth. Upload one on Outcomes and the findings about
                    people are ranked. Templates and segments are not people, and a campaign with no clear difference
                    from its held-back group has no rupees to put on it; they stay here either way.
                  </p>
                )}
              </MoreDetail>
            </section>
          )}
        </>
      )}
      {today.quiet > 0 && (
        <p className="text-[0.82rem] text-muted" data-role="today-quiet">
          {formatCount(today.quiet)} dealt with in the last {String(TODAY_QUIET_DAYS)} days —{' '}
          <Link to="/sense/actions" className="text-text underline underline-offset-4">
            see the action ledger
          </Link>
          .
        </p>
      )}
    </>
  );
}

/**
 * One finding. Only the queue's first row has the primary button — five equal
 * primaries say nothing about which to do first (review) — and only that row
 * wears the brand callout (see the header). The formula sits under the figure
 * it explains, so on a phone it never comes before it.
 */
function FindingRow({ finding, handlers, lead }: { finding: Finding; handlers: TodayHandlers; lead: boolean }) {
  return (
    <li data-role="today-finding" data-kind={finding.kind} data-lead={lead}>
      {lead ? (
        <Callout tone="brand" title="Start here">
          <FindingBody finding={finding} handlers={handlers} lead={lead} />
        </Callout>
      ) : (
        <Card>
          <CardContent className="p-[18px]">
            <FindingBody finding={finding} handlers={handlers} lead={lead} />
          </CardContent>
        </Card>
      )}
    </li>
  );
}

function FindingBody({ finding, handlers, lead }: { finding: Finding; handlers: TodayHandlers; lead: boolean }) {
  const busy = handlers.busy === finding.fingerprint;
  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
      <div className="flex min-w-0 flex-col gap-1.5">
        <p className="text-[0.95rem] font-medium leading-snug text-text">{finding.title}</p>
        <Kicker>{finding.source}</Kicker>
      </div>
      <div className="flex shrink-0 flex-col items-start gap-2 sm:max-w-[18rem] sm:items-end">
        {finding.atStake !== null && (
          <span className="flex flex-col items-start gap-0.5 sm:items-end" data-role="today-stake">
            <span className="flex items-center gap-2">
              <span className="text-[1.35rem] font-semibold leading-[1.05] tracking-[-0.03em] tabular-nums text-text">
                {formatRupees(finding.atStake.valueMinor)}
              </span>
              <Badge tone={TIER_TONE[finding.atStake.tier]}>{ATTRIBUTION_TIER_LABEL[finding.atStake.tier]}</Badge>
            </span>
            <Kicker as="span">at stake</Kicker>
            <MoreDetail summary="How the ₹ is worked out">
              <p>{finding.atStake.formula}</p>
            </MoreDetail>
          </span>
        )}
        <span className="flex flex-wrap items-center gap-2">
          <Badge tone="brand" data-role="today-pillar">
            For {PILLAR_LABELS[FINDING_PILLAR[finding.kind]]}
          </Badge>
          <Button
            size="sm"
            variant={lead ? 'primary' : 'secondary'}
            onClick={() => handlers.onTake(finding)}
            disabled={busy}
            aria-label={`${finding.action.label}: ${finding.title}`}
          >
            {finding.action.label}
          </Button>
          <Button size="sm" variant="ghost" onClick={() => handlers.onDismiss(finding)} disabled={busy} aria-label={`Not now: ${finding.title}`}>
            Not now
          </Button>
        </span>
      </div>
    </div>
  );
}
